/**
 * Authentication System Constants
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralized constants for the authentication system.
 * Defines auth states, token types, roles, permissions, and configuration.
 *
 * This file provides:
 * - Authentication states
 * - Token types and expiry times
 * - User roles and permissions
 * - Storage keys
 * - Session configuration
 * - OAuth providers
 * - i18n key prefixes
 *
 * Design Principles:
 * ------------------
 * - Single source of truth for auth configuration
 * - Type-safe constants using 'as const'
 * - Organized by category
 * - Well-documented with usage examples
 *
 * Usage:
 * ------
 * ```typescript
 * import { AUTH_STATE, USER_ROLE } from 'core/auth';
 *
 * if (authState === AUTH_STATE.AUTHENTICATED) {
 *   // User is authenticated
 * }
 * ```
 * ============================================================================
 */

/**
 * Authentication States
 *
 * Represents the current authentication status.
 */
export const AUTH_STATE = {
  UNAUTHENTICATED: 'unauthenticated',
  AUTHENTICATING: 'authenticating',
  AUTHENTICATED: 'authenticated',
  REFRESHING: 'refreshing',
  ERROR: 'error',
} as const;

/**
 * Token Types
 *
 * Types of authentication tokens used in the system.
 */
export const TOKEN_TYPE = {
  ACCESS: 'access',
  REFRESH: 'refresh',
  RESET: 'reset',
  VERIFICATION: 'verification',
} as const;

/**
 * User Roles
 *
 * System-wide user roles for authorization.
 */
export const USER_ROLE = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  USER: 'user',
  GUEST: 'guest',
} as const;

/**
 * Platform (Bimanova internal) roles
 *
 * Users with these roles access the admin portal, not the customer app.
 */
export const PLATFORM_ROLES = [
  'SUPER_ADMIN',
  'PLATFORM_SUPPORT',
  'PLATFORM_OPS',
  'PLATFORM_FINANCE',
  'REGULATORY_OFFICER',
] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/**
 * Permissions
 *
 * Granular permissions for specific actions.
 */
export const PERMISSION = {
  // User management
  USERS_VIEW: 'users:view',
  USERS_CREATE: 'users:create',
  USERS_UPDATE: 'users:update',
  USERS_DELETE: 'users:delete',

  // Policy management
  POLICIES_VIEW: 'policies:view',
  POLICIES_CREATE: 'policies:create',
  POLICIES_UPDATE: 'policies:update',
  POLICIES_DELETE: 'policies:delete',

  // Claims management
  CLAIMS_VIEW: 'claims:view',
  CLAIMS_CREATE: 'claims:create',
  CLAIMS_UPDATE: 'claims:update',
  CLAIMS_APPROVE: 'claims:approve',
  CLAIMS_REJECT: 'claims:reject',

  // Reports
  REPORTS_VIEW: 'reports:view',
  REPORTS_EXPORT: 'reports:export',

  // Settings
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_UPDATE: 'settings:update',
} as const;

/**
 * Role-Permission Mapping
 *
 * Defines which permissions each role has.
 */
export const ROLE_PERMISSIONS = {
  [USER_ROLE.SUPER_ADMIN]: [
    // All permissions
    PERMISSION.USERS_VIEW,
    PERMISSION.USERS_CREATE,
    PERMISSION.USERS_UPDATE,
    PERMISSION.USERS_DELETE,
    PERMISSION.POLICIES_VIEW,
    PERMISSION.POLICIES_CREATE,
    PERMISSION.POLICIES_UPDATE,
    PERMISSION.POLICIES_DELETE,
    PERMISSION.CLAIMS_VIEW,
    PERMISSION.CLAIMS_CREATE,
    PERMISSION.CLAIMS_UPDATE,
    PERMISSION.CLAIMS_APPROVE,
    PERMISSION.CLAIMS_REJECT,
    PERMISSION.REPORTS_VIEW,
    PERMISSION.REPORTS_EXPORT,
    PERMISSION.SETTINGS_VIEW,
    PERMISSION.SETTINGS_UPDATE,
  ],
  [USER_ROLE.ADMIN]: [
    PERMISSION.USERS_VIEW,
    PERMISSION.USERS_CREATE,
    PERMISSION.USERS_UPDATE,
    PERMISSION.POLICIES_VIEW,
    PERMISSION.POLICIES_CREATE,
    PERMISSION.POLICIES_UPDATE,
    PERMISSION.CLAIMS_VIEW,
    PERMISSION.CLAIMS_CREATE,
    PERMISSION.CLAIMS_UPDATE,
    PERMISSION.CLAIMS_APPROVE,
    PERMISSION.CLAIMS_REJECT,
    PERMISSION.REPORTS_VIEW,
    PERMISSION.REPORTS_EXPORT,
    PERMISSION.SETTINGS_VIEW,
  ],
  [USER_ROLE.MANAGER]: [
    PERMISSION.USERS_VIEW,
    PERMISSION.POLICIES_VIEW,
    PERMISSION.POLICIES_CREATE,
    PERMISSION.POLICIES_UPDATE,
    PERMISSION.CLAIMS_VIEW,
    PERMISSION.CLAIMS_CREATE,
    PERMISSION.CLAIMS_UPDATE,
    PERMISSION.REPORTS_VIEW,
  ],
  [USER_ROLE.USER]: [
    PERMISSION.POLICIES_VIEW,
    PERMISSION.CLAIMS_VIEW,
    PERMISSION.CLAIMS_CREATE,
  ],
  [USER_ROLE.GUEST]: [
    PERMISSION.POLICIES_VIEW,
  ],
} as const;

/**
 * OAuth Providers
 *
 * Supported OAuth/SSO providers.
 */
export const OAUTH_PROVIDER = {
  GOOGLE: 'google',
  MICROSOFT: 'microsoft',
  FACEBOOK: 'facebook',
} as const;

/**
 * Storage Keys
 *
 * LocalStorage/SessionStorage keys for auth data.
 */
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'bimanova_access_token',
  REFRESH_TOKEN: 'bimanova_refresh_token',
  USER_DATA: 'bimanova_user_data',
  AUTH_STATE: 'bimanova_auth_state',
  SESSION_EXPIRY: 'bimanova_session_expiry',
  REMEMBER_ME: 'bimanova_remember_me',
  LAST_LOGIN: 'bimanova_last_login',
  LOGIN_ATTEMPTS: 'bimanova_login_attempts',
} as const;

/**
 * Token Expiry Times (in milliseconds)
 */
export const TOKEN_EXPIRY = {
  ACCESS_TOKEN: 15 * 60 * 1000,      // 15 minutes
  REFRESH_TOKEN: 7 * 24 * 60 * 60 * 1000,  // 7 days
  RESET_TOKEN: 60 * 60 * 1000,      // 1 hour
  VERIFICATION_TOKEN: 24 * 60 * 60 * 1000, // 24 hours
} as const;

/**
 * Session Configuration
 */
export const SESSION_CONFIG = {
  // Maximum login attempts before lockout
  MAX_LOGIN_ATTEMPTS: 5,

  // Lockout duration (milliseconds)
  LOCKOUT_DURATION: 15 * 60 * 1000, // 15 minutes

  // Session timeout warning (milliseconds before expiry)
  SESSION_WARNING_TIME: 5 * 60 * 1000, // 5 minutes

  // Auto-refresh token time (milliseconds before expiry)
  AUTO_REFRESH_TIME: 5 * 60 * 1000, // 5 minutes

  // Check session interval (milliseconds)
  SESSION_CHECK_INTERVAL: 60 * 1000, // 1 minute

  // Remember me duration (milliseconds)
  REMEMBER_ME_DURATION: 30 * 24 * 60 * 60 * 1000, // 30 days

  // Enable token auto-refresh
  ENABLE_AUTO_REFRESH: true,

  // Enable session persistence
  ENABLE_SESSION_PERSISTENCE: true,

  // Enable activity tracking
  ENABLE_ACTIVITY_TRACKING: true,
} as const;

/**
 * Password Requirements
 */
export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 128,
  REQUIRE_UPPERCASE: true,
  REQUIRE_LOWERCASE: true,
  REQUIRE_NUMBER: true,
  REQUIRE_SPECIAL: true,
  SPECIAL_CHARACTERS: '!@#$%^&*()_+-=[]{}|;:,.<>?',
} as const;

/**
 * Authentication Events
 *
 * Events emitted by the auth system.
 */
export const AUTH_EVENT = {
  LOGIN_SUCCESS: 'auth:login_success',
  LOGIN_FAILURE: 'auth:login_failure',
  LOGOUT: 'auth:logout',
  TOKEN_REFRESHED: 'auth:token_refreshed',
  TOKEN_EXPIRED: 'auth:token_expired',
  SESSION_EXPIRED: 'auth:session_expired',
  USER_UPDATED: 'auth:user_updated',
  UNAUTHORIZED: 'auth:unauthorized',
  ACCOUNT_LOCKED: 'auth:account_locked',
} as const;

/**
 * API Endpoints
 *
 * Authentication-related API endpoints.
 */
export const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  REGISTER: '/auth/register',
  VERIFY_EMAIL: '/auth/verify-email',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  CHANGE_PASSWORD: '/auth/change-password',
  ME: '/auth/me',
  UPDATE_PROFILE: '/auth/profile',
  OAUTH_LOGIN: '/auth/oauth/:provider',
  OAUTH_CALLBACK: '/auth/oauth/:provider/callback',
} as const;

/**
 * i18n Keys
 *
 * Translation key prefixes for authentication.
 */
export const AUTH_I18N_KEYS = {
  AUTH: 'auth',
  LOGIN: 'auth:login',
  REGISTER: 'auth:register',
  LOGOUT: 'auth:logout',
  PASSWORD: 'auth:password',
  ERRORS: 'auth:errors',
  MESSAGES: 'auth:messages',
  VALIDATION: 'auth:validation',
} as const;

/**
 * Routes
 *
 * Authentication-related route paths.
 */
export const AUTH_ROUTES = {
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  VERIFY_EMAIL: '/verify-email',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  LOGOUT: '/logout',
} as const;

/**
 * Default Redirect Routes
 */
export const DEFAULT_ROUTES = {
  AFTER_LOGIN: '/dashboard',
  AFTER_LOGOUT: '/login',
  UNAUTHORIZED: '/login',
  FORBIDDEN: '/403',
} as const;

/**
 * Error Codes
 *
 * Authentication-specific error codes.
 */
export const AUTH_ERROR_CODES = {
  INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  REFRESH_FAILED: 'AUTH_REFRESH_FAILED',
  SESSION_EXPIRED: 'AUTH_SESSION_EXPIRED',
  ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  EMAIL_NOT_VERIFIED: 'AUTH_EMAIL_NOT_VERIFIED',
  PASSWORD_WEAK: 'AUTH_PASSWORD_WEAK',
  USER_NOT_FOUND: 'AUTH_USER_NOT_FOUND',
  EMAIL_ALREADY_EXISTS: 'AUTH_EMAIL_EXISTS',
  UNAUTHORIZED: 'AUTH_UNAUTHORIZED',
  FORBIDDEN: 'AUTH_FORBIDDEN',
  NETWORK_ERROR: 'AUTH_NETWORK_ERROR',
} as const;
